import { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLiveQuery } from "dexie-react-hooks";
import { useAppContext } from "../store";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { getSyncUser } from "../lib/sync/engine";
import { db } from "../lib/db";
import {
  buildPracticePlan,
  PRACTICE_SESSION_LIMIT,
  selectDueLegacyCards,
  selectHomeState,
  type PracticePlan,
} from "../lib/study";
import { build14DayHeatmap, computeStreak, dateKey, formatHeatmapDateLabel, mergeActivity } from "../lib/activity";
import { computePacksProgress, type PackProgress } from "../lib/packProgress";
import { PackProgressBar } from "../components/PackProgressBar";
import { StatCard } from "../components/StatCard";
import { QuickAddWordDialog } from "../components/QuickAddWordDialog";
import type { InstalledPack } from "../lib/packs";
import { importAssignedDeck, localAssignedDeckId } from "../lib/tutor/importDeck";
import { aiTutorEnabled } from "../lib/features";
import type { Database } from "../lib/database.types";
import {
  Play,
  Plus,
  BookOpen,
  CheckCircle2,
  Flame,
  Layers,
  Target,
  ChevronRight,
  GraduationCap,
  Download,
  Package,
  Bot,
} from "lucide-react";

type RemoteDeck = Database["public"]["Tables"]["decks"]["Row"];
type Assignment = Database["public"]["Tables"]["assignments"]["Row"] & {
  remoteDeck?: RemoteDeck;
};

export function PracticeCta({
  plan,
  onStart,
  reviewLabel,
  startLabel,
}: {
  plan: PracticePlan;
  onStart: () => void;
  reviewLabel: (count: number) => string;
  startLabel: string;
}) {
  return (
    <button
      onClick={onStart}
      className="lexibit-practice-cta mt-6 w-full max-w-xs inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3.5 text-base font-semibold text-white shadow-md shadow-indigo-200 transition-colors hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 dark:shadow-none"
    >
      <Play aria-hidden size={20} fill="currentColor" />
      {plan.total > 0 ? reviewLabel(plan.total) : startLabel}
    </button>
  );
}


function intensityClass(reviewed: number): string {
  // The lowest non-zero tier needs to read as "some activity" at a glance —
  // indigo-100/indigo-950 was too close to the empty slate tile to tell
  // apart, especially in dark mode.
  if (reviewed === 0) return "bg-slate-100 dark:bg-slate-800/50";
  if (reviewed < 5) return "bg-indigo-200 dark:bg-indigo-900/60";
  if (reviewed < 15) return "bg-indigo-300 dark:bg-indigo-800";
  if (reviewed < 30) return "bg-indigo-500 dark:bg-indigo-600";
  return "bg-indigo-700 dark:bg-indigo-400";
}

export function Dashboard() {
  const { t, i18n } = useTranslation();
  const { state } = useAppContext();
  const navigate = useNavigate();
  // "Свои слова" onboarding lands here with `?addWord=1` so the person goes
  // straight into adding their first word instead of an empty dashboard.
  const [quickAddOpen, setQuickAddOpen] = useState(
    () => new URLSearchParams(window.location.search).get("addWord") === "1",
  );
  useEffect(() => {
    if (quickAddOpen) navigate("/", { replace: true });
    // Runs once on mount to strip the one-shot query param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For the student assignments block: get userId once
  const studentUserId =
    isSupabaseConfigured && state.settings.cloudLinked && state.settings.role === "student"
      ? getSyncUser()
      : null;

  // Concept-packs live in Dexie (`db.concepts`) rather than being scoped to
  // a `state.decks` row, so onboarding-installed packs don't show up in
  // `state.decks`/`state.cards` at all. Read them here so Home can tell
  // "nothing installed" apart from "installed via a pack, not a deck", and
  // so the "Наборы" section below can render regardless of whether the user
  // also has personal decks.
  const installedPacksRow = useLiveQuery(() => db.kv.get("packs:installed"));
  const installedPacks = useMemo(
    () => (installedPacksRow?.value as InstalledPack[] | undefined) ?? [],
    [installedPacksRow],
  );
  const conceptRowsQuery = useLiveQuery(() => db.concepts.toArray(), []);
  const conceptRows = conceptRowsQuery ?? [];
  const conceptCountByPack = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of conceptRowsQuery ?? []) map.set(c.packId, (map.get(c.packId) ?? 0) + 1);
    return map;
  }, [conceptRowsQuery]);
  const homeState = selectHomeState(state.bootDone, state.decks.length, installedPacks.length);

  // Due/new counts across BOTH pack concepts and personal-deck words —
  // countDueAndNewConcepts already scans every `db.concepts` row (packs and
  // `user:<deckId>`-owned concepts alike) and every matching `cardStates`
  // row regardless of `deckId`, so this is the single unified source for
  // the "К повторению"/"Новых" stat cards below. It uses the exact same
  // candidate universe Drill queues from (see dueCounts.ts), so this number
  // and what Drill shows can never disagree. Async (Dexie), so it's fetched
  // in an effect rather than the `stats` useMemo below, which only reads
  // already-loaded `state`.
  const [practicePlan, setPracticePlan] = useState<PracticePlan | null>(null);
  useEffect(() => {
    if (homeState !== "ready") return;
    let cancelled = false;
    setPracticePlan(null);
    buildPracticePlan(
      state.settings.activeLangs,
      state.settings.activeDirections,
      PRACTICE_SESSION_LIMIT,
    ).then((plan) => {
      if (!cancelled) setPracticePlan(plan);
    });
    return () => {
      cancelled = true;
    };
  }, [homeState, state.settings.activeLangs, state.settings.activeDirections, conceptRowsQuery]);

  // Per-pack {mastered, inProgress, new, total} for the installed-packs
  // progress bars below — same helper Library's PackList uses.
  const [packProgress, setPackProgress] = useState<Map<string, PackProgress>>(new Map());
  useEffect(() => {
    if (installedPacks.length === 0) return;
    let cancelled = false;
    computePacksProgress(installedPacks).then((m) => {
      if (!cancelled) setPackProgress(m);
    });
    return () => {
      cancelled = true;
    };
  }, [installedPacks, conceptRowsQuery]);

  // Streak/heatmap read BOTH copies of the review log: `state.activity`
  // (localStorage, updated synchronously on every grade) and `db.activity`
  // (Dexie, the durable copy the same `bumpActivity()` call also writes).
  // They're supposed to mirror each other, but the two writes aren't atomic
  // — merging protects the streak from silently losing a day if the faster
  // localStorage copy ever lagged behind the durable one. See lib/activity.ts.
  const dbActivityRows = useLiveQuery(() => db.activity.toArray(), []);

  const stats = useMemo(() => {
    // "Мои слова" section below: personal-deck words specifically (not the
    // union total above) — same selection the classic Study queue uses
    // (selectDueLegacyCards), so this due count can't disagree with what
    // Study would offer if opened from a deck page.
    // Counted in WORDS (distinct concepts), not cards: `state.cards` holds one
    // row per (concept, direction), so a word studied in both directions would
    // otherwise be counted twice — and disagree with the word-granular totals
    // in the stat cards above. See countWordStats in lib/dueCounts.ts.
    const dueCards = selectDueLegacyCards(
      state.cards,
      state.settings.activeLangs,
      state.settings.activeDirections,
    );
    // Card.id is the cardState key, `${conceptId}|${direction}` (see db.ts),
    // so the part before "|" identifies the word.
    const conceptOf = (cardId: string) => cardId.split("|")[0];
    const due = new Set(dueCards.map((c) => conceptOf(c.id))).size;
    const personalWords = new Set(state.cards.map((c) => conceptOf(c.id))).size;
    const activity = mergeActivity(state.activity, dbActivityRows ?? []);
    const streak = computeStreak(activity);
    const todayActivity = activity.find((a) => a.date === dateKey(new Date()));
    const reviewedToday = todayActivity?.reviewed ?? 0;
    const correctToday = todayActivity?.correct ?? 0;
    const accuracy = todayActivity && todayActivity.reviewed > 0
      ? Math.round((todayActivity.correct / todayActivity.reviewed) * 100)
      : null;
    const heatmap = build14DayHeatmap(activity);
    return { due, personalWords, streak, reviewedToday, correctToday, accuracy, heatmap };
  }, [state.cards, state.activity, dbActivityRows, state.settings.activeLangs, state.settings.activeDirections]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t("dashboard.greetingMorning");
    if (hour < 18) return t("dashboard.greetingDay");
    return t("dashboard.greetingEvening");
  };

  if (homeState === "loading") {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 w-64 bg-slate-200 dark:bg-slate-800 rounded-xl" />
        <div className="lexibit-bento-top grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
          <div className="bg-slate-100 dark:bg-slate-900 rounded-3xl h-64" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-slate-100 dark:bg-slate-900 rounded-2xl h-24" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (homeState === "empty") {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center px-4">
        <div className="w-24 h-24 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-500 dark:text-indigo-400 rounded-full flex items-center justify-center mx-auto mb-6">
          <BookOpen size={48} strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t("dashboard.welcomeTitle")}</h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm max-w-sm mx-auto mt-2">
          {t("dashboard.welcomeDesc")}
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <Link
            to="/library"
            className="inline-flex items-center justify-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-2xl font-medium hover:bg-indigo-700 active:scale-95 transition-all"
          >
            <BookOpen size={20} />
            {t("dashboard.openLibrary")}
          </Link>
          <Link
            to="/decks"
            className="inline-flex items-center justify-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 px-6 py-3 rounded-2xl font-medium hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-slate-50 dark:hover:bg-slate-800 active:scale-95 transition-all"
          >
            <Plus size={20} />
            {t("dashboard.createManual")}
          </Link>
        </div>
        {studentUserId && (
          <div className="mt-6 w-full max-w-2xl text-left">
            <AssignmentsBlock studentId={studentUserId} />
          </div>
        )}
        {aiTutorEnabled && <AiTutorTeaserCard className="mt-6 mx-auto w-full max-w-sm" />}
      </div>
    );
  }

  // Single unified view from here on — no more legacy/concepts branching.
  // Packs (`db.concepts` + `db.conceptForms`, no `deckId`) and personal
  // words (a `state.decks` row + `deckId`-linked `cardStates`) both feed the
  // same `cardStates` table and the same Drill queue (pickDrillCards,
  // dueCounts.ts), so one hero + one set of stat cards covers both —
  // there's no scenario where installing a pack after creating a personal
  // deck (or vice versa) should hide either from Home.
  const conceptCount = conceptRows.length;
  // Quick-add needs a learning language to pick a direction for — same
  // precondition Drill itself gates on.
  const canQuickAdd = state.settings.activeLangs.length >= 2;
  return (
    <div className="lexibit-dashboard lexibit-bento">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
          {state.profile.name ? `${getGreeting()}, ${state.profile.name}!` : `${getGreeting()}!`}
        </h1>
      </header>

      <div className="lexibit-bento-top grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <section className="lexibit-practice bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 p-6 flex flex-col items-start text-start">
          <div className="lexibit-practice-icon w-12 h-12 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center">
            <BookOpen aria-hidden size={26} strokeWidth={1.8} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mt-4">
            {t("dashboard.heroTitle")}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 max-w-xs">
            {practicePlan && practicePlan.total > 0
              ? t("dashboard.heroDescPlan", {
                  count: practicePlan.total,
                  due: practicePlan.due,
                  newCount: practicePlan.new,
                })
              : stats.reviewedToday > 0
                ? t("dashboard.heroDescProgress", {
                    reviewed: stats.reviewedToday,
                    goal: state.settings.dailyGoal,
                  })
                : t("dashboard.heroDescIdle", { count: conceptCount })}
          </p>
          {practicePlan ? (
            <PracticeCta
              plan={practicePlan}
              onStart={() => navigate("/drill")}
              reviewLabel={(count) => t("dashboard.reviewDrill", { count })}
              startLabel={t("dashboard.startDrill")}
            />
          ) : (
            <div role="status" className="mt-6 flex min-h-11 w-full max-w-xs items-center justify-center text-sm text-slate-600 dark:text-slate-300">
              {t("drill.loading")}
            </div>
          )}
        </section>

        {/* Stat cards: due / new / streak / today, all over the unified
            (pack + personal-word) collection — see conceptDueCounts above. */}
        <section className="grid grid-cols-2 gap-3">
          <StatCard label={t("dashboard.statDue")} value={practicePlan?.due ?? 0} hint={t("dashboard.statDueHint")} icon={Target} tone="sky" />
          <StatCard label={t("dashboard.statNew")} value={practicePlan?.new ?? 0} hint={t("dashboard.statNewHint")} icon={Layers} tone="indigo" />
          <StatCard
            label={t("dashboard.statStreak")}
            value={stats.streak}
            hint={stats.streak === 0 ? t("dashboard.statStreakHintZero") : t("dashboard.statStreakHint")}
            icon={Flame}
            tone="amber"
          />
          <GoalCard
            reviewed={stats.reviewedToday}
            goal={state.settings.dailyGoal}
            accuracy={stats.accuracy}
          />
          {aiTutorEnabled && <AiTutorTeaserCard className="col-span-2" />}
        </section>
      </div>

      {/* Activity heatmap — driven by state.activity, which both Drill's
          gradeConceptProgress and classic Study's updateCardProgress update
          through the same bumpActivity() call, so it's already unified. */}
      <section className="lexibit-bento-activity bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("dashboard.activityTitle")}</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("dashboard.activitySubtitle")}</p>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
            <span>{t("dashboard.activityLess")}</span>
            <div className="size-3 rounded bg-slate-100 dark:bg-slate-800/50" />
            <div className="size-3 rounded bg-indigo-200 dark:bg-indigo-900/60" />
            <div className="size-3 rounded bg-indigo-300 dark:bg-indigo-800" />
            <div className="size-3 rounded bg-indigo-500 dark:bg-indigo-600" />
            <div className="size-3 rounded bg-indigo-700 dark:bg-indigo-400" />
            <span>{t("dashboard.activityMore")}</span>
          </div>
        </div>
        <div className="grid grid-cols-14 gap-1.5" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
          {stats.heatmap.map((cell) => (
            <div key={cell.date} className="flex flex-col items-center gap-1">
              <div
                className={`aspect-square w-full rounded ${intensityClass(cell.reviewed)} ${
                  cell.isToday ? "ring-2 ring-indigo-500 ring-offset-1 dark:ring-offset-slate-900" : ""
                }`}
                title={t("dashboard.deckHeatmapTitle", { date: cell.date, count: cell.reviewed })}
              />
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-between text-xs text-slate-400 dark:text-slate-500">
          <span>{formatHeatmapDateLabel(stats.heatmap[0].date, i18n.resolvedLanguage ?? i18n.language)}</span>
          <span>{formatHeatmapDateLabel(stats.heatmap[stats.heatmap.length - 1].date, i18n.resolvedLanguage ?? i18n.language)}</span>
        </div>
      </section>

      {/* Наборы: installed packs with progress bars — unaffected by whether
          the user also has personal decks. */}
      {installedPacks.length > 0 && (
        <section className="lexibit-bento-packs bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("dashboard.packsSection")}</h2>
            <Link to="/library" className="inline-flex min-h-11 items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
              {t("common.all")} →
            </Link>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {installedPacks.map((pack) => {
              const total = conceptCountByPack.get(pack.id) ?? 0;
              const progress = packProgress.get(pack.id) ?? { mastered: 0, inProgress: 0, new: total, total };
              return (
                <div
                  key={pack.id}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-800/40 p-3"
                >
                  <div className="size-10 shrink-0 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                    <Package size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{pack.name}</div>
                    <PackProgressBar progress={progress} className="mt-1.5" />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Мои слова: aggregate across every personal deck (including the
          lazily-created default "My Words" deck — see lib/words.ts), plus
          the quick-add entry point. */}
      <section className="lexibit-bento-words bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("myWords.sectionTitle")}</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {stats.personalWords > 0
                ? `${t("myWords.wordsCount", { count: stats.personalWords })} · ${t("decks.dueCount", { count: stats.due })}`
                : t("myWords.summaryZero")}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {state.decks.length > 0 && (
              <Link to="/decks" className="inline-flex min-h-11 items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
                {t("common.all")} →
              </Link>
            )}
            {canQuickAdd && (
              <button
                onClick={() => setQuickAddOpen(true)}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-indigo-50 px-3 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
              >
                <Plus size={14} /> {t("myWords.addButton")}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Assignments from tutor (students only, cloud-connected) */}
      {studentUserId && <AssignmentsBlock studentId={studentUserId} />}

      {canQuickAdd && (
        <QuickAddWordDialog open={quickAddOpen} onOpenChange={setQuickAddOpen} source="dashboard" />
      )}
    </div>
  );
}

function GoalCard({
  reviewed,
  goal,
  accuracy,
}: {
  reviewed: number;
  goal: number;
  accuracy: number | null;
}) {
  const { t } = useTranslation();
  const pct = Math.min(100, Math.round((reviewed / Math.max(goal, 1)) * 100));
  const reached = reviewed >= goal;
  return (
    <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t("dashboard.goalToday")}</span>
        <div className={`size-7 rounded-lg flex items-center justify-center ${reached ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400" : "bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400"}`}>
          <CheckCircle2 size={14} />
        </div>
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums">{reviewed}</span>
        <span className="text-sm text-slate-400">/ {goal}</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${reached ? "bg-emerald-500" : "bg-sky-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-slate-400 mt-1">
        {reached ? t("dashboard.goalReached") : accuracy !== null ? t("dashboard.goalAccuracy", { accuracy }) : t("dashboard.goalDay")}
      </div>
    </div>
  );
}

// ---------- Assignments block (student) ----------

function AssignmentsBlock({ studentId }: { studentId: string }) {
  const { t } = useTranslation();
  const targetAssignmentId = new URLSearchParams(window.location.search).get("assignment");
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from("assignments")
        .select("*")
        .eq("student_id", studentId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      if (!data || data.length === 0) {
        setAssignments([]);
        return;
      }

      // Deck ids are unique per owner only, so a plain `id` filter also
      // returns the student's own deck with the same id. Both the query and
      // the lookup key therefore carry the owner.
      const deckIds = data.map((a) => a.deck_id);
      const tutorIds = data.map((a) => a.tutor_id);
      const { data: decks } = await supabase
        .from("decks")
        .select("*")
        .in("id", deckIds)
        .in("owner_id", tutorIds);
      const deckMap = new Map((decks ?? []).map((d) => [`${d.owner_id}|${d.id}`, d]));

      setAssignments(
        data.map((a) => ({ ...a, remoteDeck: deckMap.get(`${a.tutor_id}|${a.deck_id}`) })),
      );

      // Check which are already imported
      const already = new Set<string>();
      for (const a of data) {
        const existing = await db.decks.get(localAssignedDeckId(a.tutor_id, a.deck_id));
        if (existing) already.add(a.id);
      }
      setImportedIds(already);
    } catch (err) {
      console.warn("[dashboard] assignments load error:", err);
      setLoadError(t("dashboard.assignments_loadError"));
    }
  }, [studentId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!targetAssignmentId || !assignments.some((item) => item.id === targetAssignmentId)) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`assignment-${targetAssignmentId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [assignments, targetAssignmentId]);

  if (assignments.length === 0 && !loadError) return null;

  const doImport = async (a: Assignment) => {
    if (!supabase || !a.remoteDeck) return;
    setImportingId(a.id);
    setErrors((prev) => ({ ...prev, [a.id]: "" }));
    try {
      await importAssignedDeck(supabase, a.tutor_id, a.remoteDeck);
      setImportedIds((prev) => new Set([...prev, a.id]));
    } catch (err) {
      console.warn("[dashboard] import error:", err);
      setErrors((prev) => ({ ...prev, [a.id]: t("dashboard.assignments_importError") }));
    } finally {
      setImportingId(null);
    }
  };

  return (
    <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="size-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 dark:text-emerald-400 flex items-center justify-center">
          <GraduationCap size={18} />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("dashboard.assignments_title")}</h2>
      </div>
      {loadError && (
        <p className="text-sm text-red-600 dark:text-red-400 px-1">{loadError}</p>
      )}
      <div className="space-y-3">
        {assignments.map((a) => {
          const isImported = importedIds.has(a.id);
          const isImporting = importingId === a.id;
          return (
            <div
              key={a.id}
              id={`assignment-${a.id}`}
              className={`flex items-center gap-3 rounded-xl border bg-slate-50/40 p-3 dark:bg-slate-800/40 ${
                targetAssignmentId === a.id
                  ? "border-indigo-400 ring-2 ring-indigo-200 dark:border-indigo-500 dark:ring-indigo-900"
                  : "border-slate-100 dark:border-slate-800"
              }`}
            >
              <div className="size-10 shrink-0 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 dark:text-emerald-400 flex items-center justify-center">
                <BookOpen size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {a.remoteDeck?.name ?? a.deck_id}
                </div>
                {a.note && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{a.note}</div>
                )}
                {errors[a.id] && (
                  <div className="text-xs text-red-500">{errors[a.id]}</div>
                )}
              </div>
              {isImported ? (
                <Link
                  to={`/decks/${localAssignedDeckId(a.tutor_id, a.deck_id)}`}
                  className="shrink-0 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {t("dashboard.assignments_open")}
                </Link>
              ) : (
                <button
                  onClick={() => doImport(a)}
                  disabled={isImporting || !a.remoteDeck}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50 transition-colors"
                >
                  <Download size={13} />
                  {isImporting ? t("dashboard.assignments_importing") : t("dashboard.assignments_import")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Always-visible teaser for the AI tutor — including local-only mode, where
// it sells the (cloud-gated) feature rather than hiding it. The page itself
// gates on cloud connection, so this only ever needs to link there.
function AiTutorTeaserCard({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/ai-tutor"
      className={`group flex flex-col gap-2 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors ${className}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t("dashboard.aiTutorTeaserTitle")}</span>
        <div className="size-7 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
          <Bot size={14} />
        </div>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 flex-1">{t("dashboard.aiTutorTeaserDesc")}</p>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 group-hover:gap-1.5 transition-all">
        {t("common.open")}
        <ChevronRight size={14} />
      </span>
    </Link>
  );
}
